import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import i18n from '@/i18n'
import { DAYJS_LOCALES, isAppLocale, type AppLocale } from '@/i18n/locales'
import { formatMonthDay } from '@/i18n/dateFormat'

// 扩展 dayjs 插件
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)
dayjs.extend(localizedFormat)

/**
 * 界面日期/时间按当前 i18n 语言格式化（2026-10-19 日期本地化第二批）。
 *
 * 之前这里全是 dayjs 英文模板（'MMM D, YYYY'），切到中文只换了月份名、词序还是英文的，
 * 出来是「9月 26, 2026」「4:19凌晨」。现在：
 *  - en：模板原样保留，输出逐字不变（"Sep 12, 2025" / "9:45PM" / "Sep 12, 2025, 9:45PM"）；
 *  - zh / pt / es：走 dayjs localizedFormat 的 ll / LT / lll，词序与时制由各语言 locale 决定
 *    （zh「2026年9月26日 04:19」、pt「26 de set de 2026 às 04:19」、es「26 de sep de 2026 4:19」）。
 * locale 包（zh-cn / pt-br / es）由 src/i18n/index.ts 顶部 import，这里用 `.locale()` 按实例指定，
 * 不依赖全局 dayjs.locale 是否已切（测试里不经过 languageChanged 也正确）。
 * 时区仍由调用方传的 userTimezone（useUserTimezone）决定，语言切换不影响墙钟时间。
 */
const currentLocale = (): AppLocale => (isAppLocale(i18n.language) ? i18n.language : 'en')

/** 各语言的格式模板：en 是历史模板（保持输出不变），其余是 localizedFormat token。 */
const TEMPLATES = {
  date: { en: 'MMM D, YYYY', l10n: 'll' },
  time: { en: 'h:mmA', l10n: 'LT' },
  dateTime: { en: 'MMM D, YYYY, h:mmA', l10n: 'lll' },
  dateTimeWithSeconds: { en: 'MMM D, YYYY, h:mm:ss A', l10n: 'll LTS' },
  dateTimeCompact: { en: 'MMM D, YYYY h:mmA', l10n: 'lll' },
} as const

const localized = (value: string | Date, userTimezone: string) => {
  const locale = currentLocale()
  const d = dayjs(value)
  // 非法输入以前会原样吐 "Invalid Date"，现在统一走 catch 返回 '-'
  if (!d.isValid()) throw new RangeError(`Invalid date: ${String(value)}`)
  return { locale, d: d.tz(userTimezone).locale(DAYJS_LOCALES[locale]) }
}

const formatWith = (value: string | Date, userTimezone: string, key: keyof typeof TEMPLATES): string => {
  const { locale, d } = localized(value, userTimezone)
  const tpl = TEMPLATES[key]
  return d.format(locale === 'en' ? tpl.en : tpl.l10n)
}

// 时区缩写映射
const TIMEZONE_ABBREVIATIONS: Record<string, string> = {
  'America/New_York': 'EST',
  'America/Chicago': 'CST',
  'America/Denver': 'MST',
  'America/Los_Angeles': 'PST',
  'America/Phoenix': 'MST',
  'America/Toronto': 'EST',
  'America/Vancouver': 'PST',
  'Europe/London': 'GMT',
  'Europe/Paris': 'CET',
  'Europe/Berlin': 'CET',
  'Asia/Shanghai': 'CST',
  'Asia/Tokyo': 'JST',
  'Asia/Hong_Kong': 'HKT',
  'Asia/Singapore': 'SGT',
  'Australia/Sydney': 'AEDT',
  'Australia/Melbourne': 'AEDT',
  'Pacific/Auckland': 'NZDT',
}

/**
 * 获取时区缩写
 * @param timezone IANA 时区名称
 * @param date 可选的日期，用于处理夏令时
 * @returns 时区缩写
 */
export const getTimezoneAbbreviation = (timezone: string, date?: Date | string): string => {
  // 首先检查预定义的缩写
  if (TIMEZONE_ABBREVIATIONS[timezone]) {
    return TIMEZONE_ABBREVIATIONS[timezone]
  }
  
  // 使用 dayjs 获取时区缩写
  const dayjsDate = date ? dayjs(date).tz(timezone) : dayjs().tz(timezone)
  const abbr = dayjsDate.format('z')
  
  // 如果返回的是 GMT+X 格式，尝试使用更友好的格式
  if (abbr.startsWith('GMT')) {
    const offset = dayjsDate.format('Z') // 获取 +08:00 格式
    return `UTC${offset}`
  }
  
  return abbr
}

/**
 * 格式化日期（不含时间）
 * @param dateString ISO 8601 格式的日期字符串
 * @param userTimezone 用户时区（IANA 格式）
 * @returns 格式化后的日期字符串，如 "Sep 12, 2025"
 */
export const formatDate = (
  dateString?: string | Date | null,
  userTimezone: string = 'America/New_York'
): string => {
  if (!dateString) return '-'
  
  try {
    // 在用户时区取日历日，按当前语言格式化："Sep 12, 2025" / "2025年9月12日"
    return formatWith(dateString, userTimezone, 'date')
  } catch (error) {
    console.error('Error formatting date:', error)
    return '-'
  }
}

/**
 * 格式化时间（不含日期）
 * @param dateString ISO 8601 格式的日期时间字符串
 * @param userTimezone 用户时区（IANA 格式）
 * @param showTimezone 是否显示时区缩写
 * @returns 格式化后的时间字符串，如 "9:45 PM PST"
 */
export const formatTime = (
  dateString?: string | Date | null,
  userTimezone: string = 'America/New_York',
  showTimezone: boolean = true
): string => {
  if (!dateString) return '-'
  
  try {
    // en 12 小时制 "9:45PM"；其他语言按 locale 的 LT（zh "21:45"）
    let timeStr = formatWith(dateString, userTimezone, 'time')

    // 添加时区缩写
    if (showTimezone) {
      const tzAbbr = getTimezoneAbbreviation(userTimezone, dateString)
      timeStr = `${timeStr} ${tzAbbr}`
    }
    
    return timeStr
  } catch (error) {
    console.error('Error formatting time:', error)
    return '-'
  }
}

/**
 * 格式化日期和时间
 * @param dateString ISO 8601 格式的日期时间字符串
 * @param userTimezone 用户时区（IANA 格式）
 * @param showTimezone 是否显示时区缩写
 * @returns 格式化后的日期时间字符串，如 "Sep 12, 2025, 9:45 PM PST"
 */
export const formatDateTime = (
  dateString?: string | Date | null,
  userTimezone: string = 'America/New_York',
  showTimezone: boolean = true
): string => {
  if (!dateString) return '-'

  try {
    // en "Sep 12, 2025, 9:45PM"；zh "2025年9月12日 21:45"
    let dateTimeStr = formatWith(dateString, userTimezone, 'dateTime')

    // 添加时区缩写
    if (showTimezone) {
      const tzAbbr = getTimezoneAbbreviation(userTimezone, dateString)
      dateTimeStr = `${dateTimeStr} ${tzAbbr}`
    }

    return dateTimeStr
  } catch (error) {
    console.error('Error formatting datetime:', error)
    return '-'
  }
}

/**
 * 格式化日期和时间（精确到秒）
 * 适用于调度任务等需要精确时间的场景
 * @param dateString ISO 8601 格式的日期时间字符串
 * @param userTimezone 用户时区（IANA 格式）
 * @param showTimezone 是否显示时区缩写
 * @returns 格式化后的日期时间字符串，如 "Sep 12, 2025, 9:45:30 PM PST"
 */
export const formatDateTimeWithSeconds = (
  dateString?: string | Date | null,
  userTimezone: string = 'America/New_York',
  showTimezone: boolean = false
): string => {
  if (!dateString) return '-'

  try {
    // en "Sep 12, 2025, 9:45:30 PM"；zh "2025年9月12日 21:45:30"
    let dateTimeStr = formatWith(dateString, userTimezone, 'dateTimeWithSeconds')

    // 添加时区缩写
    if (showTimezone) {
      const tzAbbr = getTimezoneAbbreviation(userTimezone, dateString)
      dateTimeStr = `${dateTimeStr} ${tzAbbr}`
    }

    return dateTimeStr
  } catch (error) {
    console.error('Error formatting datetime with seconds:', error)
    return '-'
  }
}

/**
 * 格式化短日期时间（用于表格等紧凑显示）
 * @param dateString ISO 8601 格式的日期时间字符串
 * @param userTimezone 用户时区（IANA 格式）
 * @returns 格式化后的短日期时间，如 "Sep 12, 9:45 PM"
 */
export const formatDateTimeShort = (
  dateString?: string | Date | null,
  userTimezone: string = 'America/New_York'
): string => {
  if (!dateString) return '-'
  
  try {
    const { locale, d } = localized(dateString, userTimezone)
    if (locale === 'en') return d.format('MMM D, h:mm A')
    // 其他语言：月日交给 Intl（zh "9月12日" / pt "12 de set."），时间用 locale 的 LT
    return `${formatMonthDay(d.toDate(), locale, userTimezone)} ${d.format('LT')}`
  } catch (error) {
    console.error('Error formatting short datetime:', error)
    return '-'
  }
}

/**
 * 格式化日期时间为紧凑格式（如 "Sep 23, 2025 3:18pm"）
 * @param dateString ISO 8601 格式的日期时间字符串
 * @param userTimezone 用户时区（IANA 格式）
 * @returns 格式化后的日期时间，如 "Sep 23, 2025 3:18pm"
 */
export const formatDateTimeCompact = (
  dateString?: string | Date | null,
  userTimezone: string = 'America/New_York'
): string => {
  if (!dateString) return '-'
  
  try {
    const { locale, d } = localized(dateString, userTimezone)
    if (locale === 'en') return d.format(TEMPLATES.dateTimeCompact.en).toLowerCase()
    return d.format(TEMPLATES.dateTimeCompact.l10n)
  } catch (error) {
    console.error('Error formatting compact datetime:', error)
    return '-'
  }
}

/**
 * 格式化相对时间（如 "2 hours ago", "in 3 days"）
 * @param dateString ISO 8601 格式的日期时间字符串
 * @param userTimezone 用户时区（IANA 格式）
 * @returns 相对时间字符串
 */
export const formatRelativeTime = (
  dateString?: string | Date | null,
  userTimezone: string = 'America/New_York'
): string => {
  if (!dateString) return '-'
  
  try {
    const date = dayjs(dateString).tz(userTimezone)
    const now = dayjs().tz(userTimezone)
    const diffInSeconds = now.diff(date, 'second')
    const absDiff = Math.abs(diffInSeconds)
    
    // 判断是过去还是未来
    const isFuture = diffInSeconds < 0
    
    // 根据时间差返回相应的格式
    if (absDiff < 60) {
      return isFuture ? i18n.t('common:time.inFewSeconds') : i18n.t('common:time.fewSecondsAgo')
    } else if (absDiff < 3600) {
      const minutes = Math.floor(absDiff / 60)
      return isFuture
        ? i18n.t('common:time.inMinute', { count: minutes })
        : i18n.t('common:time.minuteAgo', { count: minutes })
    } else if (absDiff < 86400) {
      const hours = Math.floor(absDiff / 3600)
      return isFuture
        ? i18n.t('common:time.inHour', { count: hours })
        : i18n.t('common:time.hourAgo', { count: hours })
    } else if (absDiff < 604800) {
      const days = Math.floor(absDiff / 86400)
      return isFuture
        ? i18n.t('common:time.inDay', { count: days })
        : i18n.t('common:time.dayAgo', { count: days })
    } else if (absDiff < 2592000) {
      const weeks = Math.floor(absDiff / 604800)
      return isFuture
        ? i18n.t('common:time.inWeek', { count: weeks })
        : i18n.t('common:time.weekAgo', { count: weeks })
    } else {
      // 超过一个月，显示具体日期
      return formatDate(dateString, userTimezone)
    }
  } catch (error) {
    console.error('Error formatting relative time:', error)
    return '-'
  }
}

/**
 * 格式化持续时间（秒转换为可读格式）
 * @param seconds 持续时间（秒）
 * @returns 格式化后的持续时间，如 "2h 30m" 或 "45m"
 */
export const formatDuration = (seconds?: number | null): string => {
  if (!seconds || seconds <= 0) return '-'
  
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60
  
  if (hours > 0) {
    if (minutes > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${hours}h`
  } else if (minutes > 0) {
    if (remainingSeconds > 0) {
      return `${minutes}m ${remainingSeconds}s`
    }
    return `${minutes}m`
  } else {
    return `${remainingSeconds}s`
  }
}

/**
 * 格式化持续时间为 HH:MM:SS 格式
 * @param seconds 持续时间（秒）
 * @returns 格式化后的持续时间，如 "02:30:45"
 */
export const formatDurationHMS = (seconds?: number | null): string => {
  if (!seconds || seconds <= 0) return '00:00:00'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
}

/**
 * 格式化持续时间为 HH:MM 格式（不含秒）
 * @param seconds 持续时间（秒）
 * @returns 格式化后的持续时间，如 "02:30"
 */
export const formatDurationHM = (seconds?: number | null): string => {
  if (!seconds || seconds <= 0) return '00:00'

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

/**
 * 判断日期是否为今天
 * @param dateString ISO 8601 格式的日期字符串
 * @param userTimezone 用户时区（IANA 格式）
 * @returns 是否为今天
 */
export const isToday = (
  dateString?: string | Date | null,
  userTimezone: string = 'America/New_York'
): boolean => {
  if (!dateString) return false
  
  try {
    const date = dayjs(dateString).tz(userTimezone)
    const today = dayjs().tz(userTimezone)
    return date.isSame(today, 'day')
  } catch (error) {
    console.error('Error checking if date is today:', error)
    return false
  }
}

/**
 * 判断日期是否为明天
 * @param dateString ISO 8601 格式的日期字符串
 * @param userTimezone 用户时区（IANA 格式）
 * @returns 是否为明天
 */
export const isTomorrow = (
  dateString?: string | Date | null,
  userTimezone: string = 'America/New_York'
): boolean => {
  if (!dateString) return false
  
  try {
    const date = dayjs(dateString).tz(userTimezone)
    const tomorrow = dayjs().tz(userTimezone).add(1, 'day')
    return date.isSame(tomorrow, 'day')
  } catch (error) {
    console.error('Error checking if date is tomorrow:', error)
    return false
  }
}

/**
 * 获取智能日期显示（今天、明天、具体日期）
 * @param dateString ISO 8601 格式的日期字符串
 * @param userTimezone 用户时区（IANA 格式）
 * @returns 智能日期字符串
 */
export const getSmartDate = (
  dateString?: string | Date | null,
  userTimezone: string = 'America/New_York'
): string => {
  if (!dateString) return '-'

  if (isToday(dateString, userTimezone)) {
    return i18n.t('common:time.today')
  } else if (isTomorrow(dateString, userTimezone)) {
    return i18n.t('common:time.tomorrow')
  } else {
    return formatDate(dateString, userTimezone)
  }
}

/**
 * 格式化日期为 YYYY-MM-DD（用于 API 请求）
 * 将 Date 对象在用户时区格式化为 API 所需的日期字符串
 *
 * @param date Date 对象或日期字符串
 * @param userTimezone 用户时区（IANA 格式）
 * @returns 格式化后的日期字符串，如 "2025-12-25"
 *
 * @example
 * // Date 对象是用户时区 2025-12-25 12:00 创建的
 * const date = dayjs.tz("2025-12-25", "Asia/Shanghai").hour(12).toDate()
 * formatDateForAPI(date, "Asia/Shanghai") // "2025-12-25"
 */
export const formatDateForAPI = (
  date: Date | string,
  userTimezone: string = 'America/New_York'
): string => {
  // Date 对象内部存储 UTC 时间戳，需要在用户时区解释才能得到正确的日期
  return dayjs(date).tz(userTimezone).format('YYYY-MM-DD')
}

/**
 * 履约/readiness task 的默认排期日（YYYY-MM-DD）：
 * 入住时间戳按 timezone 取日历日；入住日已过则取今天（在住场景，
 * task 不排到过去）。无 checkIn 返回 null（调用方不预填，让用户自选）。
 *
 * 三个 Create task 入口共用同一口径：UpsellRequestDetail Fulfillment 区块、
 * readiness 看板 PropertyCard、ReservationDetail ReadinessSection。
 */
export const plannedDateForCheckIn = (
  checkIn: string | null | undefined,
  userTimezone: string = 'America/New_York'
): string | null => {
  if (!checkIn) return null
  const checkInDate = dayjs(checkIn).tz(userTimezone).format('YYYY-MM-DD')
  const today = dayjs().tz(userTimezone).format('YYYY-MM-DD')
  // YYYY-MM-DD 字符串比较即日期比较
  return checkInDate < today ? today : checkInDate
}